import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "antd";
import OrderDTO from "@/dto/order/OrderDTO";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import useProductQuantityPerPackagesInfo from "@/scenes/OrderManagement/components/dialogs/PrintWaybillDialog/hooks/useProductQuantityPerPackagesInfo";
import PrintWaybillForm from "./PrintWaybillForm";
import { useGlobalStore } from "@/store/hooks";
import { useModalStore } from "@/hooks/Modal/hooks";
import { MultiShopSelectPlatformType } from "@/components/MultiShopSelect/types";
import ShopType from "@/enum/ShopType";
import { observer } from "mobx-react-lite";
import OrderSellerRemarksDTO from "@/dto/order/OrderSellerRemarksDTO";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import MultiPlatformPrintWaybillModalStore from "./store";
import { cloneDeep, uniqBy } from "lodash";
import type SupplierPartnershipsStore from "@/store/SupplierPartnershipsStore";

interface MultiPlatformPrintWaybillModalProps extends InjectBaseModalProps {
    orders: OrderDTO[];
    defaultTemplateId?: number;
    onUpdateOrder: (id: number, params: Partial<OrderDTO>) => void;
    onShipTaskDone: () => void;
    /**
     * 确定订单属于哪种平台的判断函数
     * 1.如果是供货商的'分销店铺'使用该弹窗，需要从分销信息中获取订单来源的平台进行分类
     * 2.如果订单属于代理商店铺，直接从订单所属店铺中提取平台信息。
     */
    multiPlatformModalOrderOriginCheck?: (order: OrderDTO) => ShopType;
    onUpdateOrderSellerRemarks?: (orderId: number, sellerRemarks: OrderSellerRemarksDTO) => void;
    onUpdateSupplierOrderRemarks?: (supplierOrderId: number, dropshippingRemarks: string) => void;
    /** 如果订单都来源于一个店铺，则传入店铺id */
    shopId?: number;

    showProcessInRange?: boolean;

    supplierPartnershipsStore?: SupplierPartnershipsStore;
}

export interface PlatformOrderInfo {
    [MultiShopSelectPlatformType.tb]: OrderDTO[];
    [MultiShopSelectPlatformType.pdd]: OrderDTO[];
    [MultiShopSelectPlatformType.dy]: OrderDTO[];
    [MultiShopSelectPlatformType.jd]: OrderDTO[];
    [MultiShopSelectPlatformType.ks]: OrderDTO[];
    [MultiShopSelectPlatformType.ali]: OrderDTO[];
    [MultiShopSelectPlatformType.xhs]: OrderDTO[];
    [MultiShopSelectPlatformType.channelShop]: OrderDTO[];
    [MultiShopSelectPlatformType.wb]: OrderDTO[];
    [MultiShopSelectPlatformType.other]: OrderDTO[];

    [MultiShopSelectPlatformType.other_drop_shipping_supplier_ks]: OrderDTO[];
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_xhs]: OrderDTO[];
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_channel_shop]: OrderDTO[];
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_wb]: OrderDTO[];
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_other]: OrderDTO[];

    [MultiShopSelectPlatformType.ali_out_order_taobao]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_out_order_pdd]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_out_order_dy]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_out_order_kwai]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_out_order_xhs]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_out_order_jd]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_out_order_weixin]: OrderDTO[];

    [MultiShopSelectPlatformType.ali_plaintext_out_order_taobao]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_plaintext_out_order_pdd]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_plaintext_out_order_dy]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_plaintext_out_order_kwai]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_plaintext_out_order_xhs]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_plaintext_out_order_jd]: OrderDTO[];
}

export const platforms = Object.keys(MultiShopSelectPlatformType) as MultiShopSelectPlatformType[];

/**
 * 多店铺打印面单弹框
 */
const MultiPlatformPrintWaybillModal: React.FC<MultiPlatformPrintWaybillModalProps> = function (props) {
    const globalStore = useGlobalStore();
    const {
        open,
        onCancel,
        orders: propOrders,
        defaultTemplateId,
        onUpdateOrder,
        onShipTaskDone,
        multiPlatformModalOrderOriginCheck,
        onUpdateOrderSellerRemarks,
        onUpdateSupplierOrderRemarks,
        shopId,
        showProcessInRange,
        supplierPartnershipsStore,
    } = props;
    const modalStore = useModalStore();
    const [form] = Form.useForm();
    const [orders] = useState<OrderDTO[]>(() => {
        return cloneDeep(uniqBy(propOrders, "id"));
    });
    const {
        canUseMaxQuantityPerPackageOrderCount,
        loading: perPackageSettingLoading,
    } = useProductQuantityPerPackagesInfo(orders);

    const [multiPlatformPrintWaybillModalStore] = useState(
        () =>
            new MultiPlatformPrintWaybillModalStore({
                globalStore,
                modalStore,
                orders,
                form,
                defaultTemplateId,
                onCancel,
                onShipTaskDone,
                onUpdateOrder,
                multiPlatformModalOrderOriginCheck,
                onUpdateOrderSellerRemarks,
                onUpdateSupplierOrderRemarks,
                shopId,
                canUseMaxQuantityPerPackageOrderCount,
                supplierPartnershipsStore,
            }),
    );

    useEffect(() => {
        globalStore.ewaybillAccountStore.ensure();
    }, []);

    return (
        <Modal
            width={968}
            open={open}
            onCancel={onCancel}
            mask={{ closable: false }}
            title={
                <div>
                    <span>打印快递单</span>
                    <span className="text-gray text-small">
                        （ {orders.length}个订单
                        {canUseMaxQuantityPerPackageOrderCount
                            ? `,其中${canUseMaxQuantityPerPackageOrderCount}个商品可自动分包`
                            : null}
                        ）
                    </span>
                </div>
            }
            footer={
                <>
                    <AutoLoadingButton
                        key="print"
                        type="primary"
                        loading={perPackageSettingLoading}
                        onClick={multiPlatformPrintWaybillModalStore.handlePrintBtnClick}
                    >
                        打印
                    </AutoLoadingButton>

                    <Button key="close" onClick={onCancel}>
                        关闭
                    </Button>
                </>
            }
        >
            <PrintWaybillForm
                form={form}
                orders={orders}
                defaultTemplateId={defaultTemplateId}
                platformOrderInfo={multiPlatformPrintWaybillModalStore.platformOrderInfo}
                shopId={shopId}
                showProcessInRange={showProcessInRange}
                supplierPartnershipsStore={supplierPartnershipsStore}
            />
        </Modal>
    );
};

export default observer(MultiPlatformPrintWaybillModal);
